import type { LoginResponse } from "../types/api.js";
import {
  clearAuthStorage,
  getStoredAccessExpiresAtMs,
  getStoredRefreshToken,
  setStoredAccessExpiresAtMs,
  setStoredRefreshToken,
  setStoredToken,
} from "../lib/storage-token.js";
import { notifySessionInvalidated, notifyTokensRefreshed } from "../lib/auth-session-bridge.js";

function joinBase(path: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  if (path.startsWith("http")) return path;
  const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${prefix}${p}`;
}

function isAuthRefreshExcludedPath(path: string): boolean {
  return (
    path.includes("/api/v1/auth/login") ||
    path.includes("/api/v1/auth/refresh") ||
    path.includes("/api/v1/auth/logout") ||
    path.includes("/api/v1/auth/me/change-password")
  );
}

export function shouldAttemptSilentRefreshOn401(path: string): boolean {
  return !isAuthRefreshExcludedPath(path);
}

const PROACTIVE_REFRESH_SKEW_MS = 50_000;

export async function ensureFreshAccessIfNeeded(): Promise<void> {
  const expiresAt = getStoredAccessExpiresAtMs();
  if (expiresAt === null) return;
  if (expiresAt - Date.now() > PROACTIVE_REFRESH_SKEW_MS) return;
  if (!getStoredRefreshToken()) return;
  await trySilentRefreshAndPersist();
}

export async function trySilentRefreshAndPersist(): Promise<boolean> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    clearAuthStorage();
    notifySessionInvalidated();
    return false;
  }

  try {
    const res = await fetch(joinBase("/api/v1/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    const text = await res.text();
    let data: unknown;
    try {
      data = text ? (JSON.parse(text) as unknown) : undefined;
    } catch {
      data = undefined;
    }

    if (!res.ok || typeof data !== "object" || data === null) {
      clearAuthStorage();
      notifySessionInvalidated();
      return false;
    }

    const body = data as LoginResponse;
    const access = body.accessToken ?? body.token;
    const nextRefresh = body.refreshToken;
    if (typeof access !== "string" || typeof nextRefresh !== "string") {
      clearAuthStorage();
      notifySessionInvalidated();
      return false;
    }

    setStoredToken(access);
    setStoredRefreshToken(nextRefresh);
    if (typeof body.expiresIn === "number" && body.expiresIn > 0) {
      setStoredAccessExpiresAtMs(Date.now() + body.expiresIn * 1000);
    }
    notifyTokensRefreshed(access, nextRefresh);
    return true;
  } catch {
    clearAuthStorage();
    notifySessionInvalidated();
    return false;
  }
}
